import { Box, Button, Typography } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import type { CreditNote, V2InvoiceDetailResponse } from "../types/invoice";
import { convertToINR, convertDate } from "@/helpers/Utils";
import { V2_COLORS } from "../constants/colors";

interface PODetailsForMilestone {
  source: string;
  milestone: string;
  invoices: Array<{ invoice_no?: string }>;
}

interface InvoiceDetailsCardProps {
  detail: V2InvoiceDetailResponse;
  onReview?: () => void;
  poDetails?: PODetailsForMilestone;
  showExtras?: boolean;
  onCreditNote?: (creditNote: CreditNote) => void;
  amountPaid?: number | null;
  paymentDate?: string | null;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box display="flex" alignItems="center" gap={3} py={1}>
      <Typography variant="subtitle1" sx={{ width: 120, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body1">
        {value ?? "\u2014"}
      </Typography>
    </Box>
  );
}

function getMilestoneLabel(
  milestone: string,
  invoiceNo: string,
  invoices: Array<{ invoice_no?: string }>,
  invoiceValue: number,
): string {
  const milestoneArr = milestone.split("-");
  const index = invoices.findIndex((inv) => inv.invoice_no === invoiceNo);
  const idx = index >= 0 ? index : invoices.length;
  const percentage = milestoneArr[idx];
  if (!percentage) return "";
  const n = idx + 1;
  const total = milestoneArr.length;
  const amount = convertToINR(invoiceValue);
  return `Milestone ${n} of ${total} (${percentage}%) | ${amount}`;
}

export default function InvoiceDetailsCard({
  detail,
  onReview,
  poDetails,
  showExtras = false,
  onCreditNote,
  amountPaid,
  paymentDate,
}: InvoiceDetailsCardProps) {
  const { invoice, vendor_name } = detail;
  const creditNote = detail.credit_notes?.[0];
  const invoiceCopyUrl = detail.invoice_image?.pre_signed_url;

  const milestoneLabel =
    poDetails?.source === "FINS" && poDetails.milestone
      ? getMilestoneLabel(poDetails.milestone, invoice.invoice_no, poDetails.invoices, invoice.invoice_value)
      : "";

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="h4">Invoice details</Typography>
        {onReview && (
          <Button
            variant="outlined"
            size="small"
            endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
            onClick={onReview}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13px",
              borderColor: V2_COLORS.primary,
              color: V2_COLORS.primary,
              "&:hover": { borderColor: V2_COLORS.primary, backgroundColor: "rgba(54, 85, 242, 0.04)" },
            }}
          >
            Review
          </Button>
        )}
      </Box>
      <Box sx={{ bgcolor: "background.paper", borderRadius: "8px", py: 2, pl: 1.5, pr: 3 }}>
        <Row label="Invoice no." value={invoice.invoice_no} />
        <Row label="Date" value={convertDate(invoice.invoice_date)} />
        <Row label="Vendor" value={vendor_name} />
        <Row label="Invoice amount" value={convertToINR(invoice.invoice_value)} />
        {milestoneLabel && <Row label="Milestone" value={milestoneLabel} />}
        {showExtras && (
          <>
            <Row
              label="Amount paid"
              value={
                amountPaid == null
                  ? null
                  : `${convertToINR(amountPaid)}${paymentDate ? ` [${convertDate(paymentDate)}]` : ""}`
              }
            />
            <Row label="Addl. charges" value={convertToINR(invoice.additional_charges ?? 0)} />
            <Row label="IRN" value={invoice.irn_invoice ? "Yes" : "No"} />
            <Row
              label="Credit note"
              value={
                creditNote ? (
                  <Button
                    variant="text"
                    onClick={() => onCreditNote?.(creditNote)}
                    sx={{
                      p: 0,
                      minWidth: 0,
                      font: "inherit",
                      textTransform: "none",
                      textDecoration: "underline",
                      color: V2_COLORS.primary,
                    }}
                  >
                    {creditNote.number}
                  </Button>
                ) : null
              }
            />
            <Row
              label="Invoice copy"
              value={
                invoiceCopyUrl ? (
                  <a href={invoiceCopyUrl} target="_blank" rel="noreferrer" style={{ color: V2_COLORS.primary }}>
                    {detail.invoice_image.file_name || "Invoice copy"}
                  </a>
                ) : null
              }
            />
          </>
        )}
      </Box>
    </Box>
  );
}
